import axios from "axios";
import type { GroupNode, GroupUser } from "@/interfaces/group";
import { apiClient } from "@/lib/apiClient";

function asError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return new Error(error.response.data.error as string);
  }
  return error instanceof Error ? error : new Error(fallback);
}

export async function fetchGroups(organizationId: number): Promise<GroupNode[]> {
  const { data } = await apiClient.get<{ success: boolean; data: GroupNode[] }>("/admin/groups", {
    params: { organizationId },
  });
  return data.data ?? [];
}

export async function createGroup(input: {
  organizationId: number;
  parentId: number;
  name: string;
  creditPurchaseAllowed: boolean;
}): Promise<number> {
  try {
    const { data } = await apiClient.post<{ success: boolean; id: number }>("/admin/groups", input);
    return data.id;
  } catch (error) {
    throw asError(error, "Failed to create group. Please try again.");
  }
}

export async function updateGroup(
  id: number,
  patch: { name?: string; creditPurchaseAllowed?: boolean },
): Promise<void> {
  try {
    await apiClient.patch(`/admin/groups/${id}`, patch);
  } catch (error) {
    throw asError(error, "Failed to update group. Please try again.");
  }
}

export async function deleteGroup(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/groups/${id}`);
  } catch (error) {
    throw asError(error, "Failed to remove group. Please try again.");
  }
}

export async function fetchGroupUsers(groupId: number): Promise<GroupUser[]> {
  const { data } = await apiClient.get<{ success: boolean; data: GroupUser[] }>(
    `/admin/groups/${groupId}/users`,
  );
  return data.data ?? [];
}

export async function addGroupUser(groupId: number, userId: number, role: string): Promise<void> {
  try {
    await apiClient.post(`/admin/groups/${groupId}/users`, { userId, role });
  } catch (error) {
    throw asError(error, "Failed to add user to group. Please try again.");
  }
}

export async function removeGroupUser(groupId: number, userId: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/groups/${groupId}/users/${userId}`);
  } catch (error) {
    throw asError(error, "Failed to remove user from group. Please try again.");
  }
}
